<!-- Quiz Panel Content -->
<div class="quiz-content p-4" #quizContent>
  <!-- Quiz Header -->
  <div class="quiz-header pb-4">
    <div class="flex flex-wrap justify-between items-start gap-3">
      <div class="min-w-0">
        <h3 class="text-lg font-semibold">
          <app-icon name="quiz" size="sm" class="text-secondary"></app-icon>
          Module Quiz
        </h3>
        <p class="text-sm text-base-content/70 mt-1">
          {{ (videoId || videoUrl) ? 'Answer the questions as you watch the video' : 'Complete the following quiz' }}
        </p>
      </div>
      <!-- KAN-561 + KAN-565 are the SAME squeeze with two repairs, and this is
           the reconciled shape (see the SCSS rule for the full story):
             whitespace-nowrap + shrink-0 (KAN-561) — the pill keeps its
               max-content width and its label can never break into a second
               line that paints outside a pinned 1.5rem box;
             flex-wrap on the row above + min-w-0 on the title column — when
               that unshrinkable pill no longer fits beside the title, it drops
               onto its OWN ROW instead of overlapping it or pushing the 340px
               quiz panel sideways (the KAN-231 precedent);
             quiz-points-badge — containment insurance for a label that wraps
               anyway, and the marker for the SCSS rule. -->
      <div class="badge badge-secondary badge-lg whitespace-nowrap shrink-0 ml-auto quiz-points-badge"
           data-testid="quiz-panel-points-badge"
           *ngIf="quizQuestions.length > 0">
        {{ totalPointsLabel }}
      </div>
    </div>
    <!-- KAN-224: instructor-authored quiz description with clickable links
         (escape-first linkify — URLs and [text](url) become safe anchors). -->
    <div *ngIf="quizDescription && quizQuestions.length > 0"
         class="quiz-description text-sm text-base-content/80 mt-2"
         data-testid="quiz-panel-description"
         [innerHTML]="quizDescription | linkify"></div>
  </div>

  <!-- Learner-facing Video Requirement Notice (instructor "Override Active" banner
       moved to the consolidated header strip in preview modals). -->
  <div *ngIf="isVideoRequired && !videoCompleted && quizQuestions.length > 0 && !canOverrideVideoRequirement()"
       class="mb-4">
    <div class="alert alert-info py-2 px-3" data-testid="quiz-video-hard-block">
      <app-icon name="info" size="xs"></app-icon>
      <div class="flex-1">
        <div class="text-sm font-semibold">Please finish watching the video before submitting your quiz.</div>
        <!-- KAN-306: the shared chip states watch progress here exactly as it
             does in the player and the status bar. It replaced a bespoke
             progress bar and a "Progress: N% watched" caption -- a third
             renderer of one metric, and the one still naming the retired
             playhead-flavoured "% watched". The chip is its own gauge, so the
             separate bar is gone; the helper carries what is actionable (how
             much MORE, and the threshold it is measured against). -->
        <div class="flex items-center gap-2 flex-wrap mt-1">
          <app-watch-progress-chip
            size="sm"
            [percent]="videoWatchPercentage"
            [completed]="videoCompleted"></app-watch-progress-chip>
          <span class="text-xs opacity-75">{{ unlockHelperText }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- KAN-332 QA Fix 4: a 403 MODULE_LOCKED/UNIT_LOCKED on attempt-start clears
       quizQuestions so nothing below can render it - this banner is the ONLY
       thing shown in that state (never the "No Quiz Available" message below,
       which would wrongly imply there's no quiz at all). -->
  <div *ngIf="moduleLocked" class="alert alert-warning" data-testid="quiz-panel-locked-alert">
    <app-icon name="lock" size="sm"></app-icon>
    <span>{{ moduleLockedReason }}</span>
  </div>

  <!-- No Quiz Available -->
  <!-- KAN-196: When the watch requirement is "None" (or there is no video gating
       at all), the legacy "Watch the video to complete the module" message is
       false. Use !isVideoRequired so the friendly message always appears in
       that case, regardless of whether videoCompleted has propagated yet. -->
  <div *ngIf="quizQuestions.length === 0 && !moduleLocked" class="alert" data-testid="quiz-panel-no-quiz-alert">
    <app-icon name="info" size="sm"></app-icon>
    <span *ngIf="!isVideoRequired">This module does not have a quiz. You can complete this module now.</span>
    <span *ngIf="isVideoRequired">This module does not have a quiz. Watch the video to complete the module.</span>
  </div>

  <!-- Submission Date Banner -->
  <div *ngIf="isViewingPreviousSubmission && submissionDate" class="submission-info-banner alert alert-info">
    <app-icon name="history" size="sm"></app-icon>
    <!-- KAN-334: a granted submission has no real attempt — label it clearly. -->
    <span *ngIf="viewingManualGrant && !viewingPriorCredit" data-testid="quiz-panel-granted-banner">Credited by instructor (no attempt) &middot; {{ submissionDate | date:'medium' }}</span>
    <!-- KAN-627: prior completion credit -->
    <span *ngIf="viewingPriorCredit" data-testid="quiz-panel-granted-banner">Credited for prior completion (no attempt) &middot; {{ submissionDate | date:'medium' }}</span>
    <span *ngIf="!viewingManualGrant">Viewing your submission from {{ submissionDate | date:'medium' }}</span>
  </div>

  <!-- Question Mismatch Warning -->
  <div *ngIf="showQuestionMismatchBanner" class="question-mismatch-banner alert alert-warning">
    <app-icon name="warning" size="sm"></app-icon>
    <span>&#x26A0;&#xFE0F; Some questions may have changed since your submission</span>
  </div>

  <!-- KAN-348: attempt start failure. Bank/shuffle quizzes fail CLOSED
       (submit disabled via attemptSubmitBlocked); Retry re-calls startAttempt. -->
  <div *ngIf="attemptLoadError && !quizSubmitted" class="alert alert-error" data-testid="attempt-load-error">
    <app-icon name="error" size="sm"></app-icon>
    <span class="flex-1">
      {{ attemptLoadError }}
      <ng-container *ngIf="attemptSubmitBlocked"> This quiz draws its questions from a question bank, so it can't be submitted until it loads.</ng-container>
    </span>
    <button type="button" class="btn btn-sm" data-testid="attempt-retry-button" (click)="retryAttemptStart()">Retry</button>
  </div>

  <!-- KAN-678: the instructor changed this quiz after the learner opened it -->
  <div *ngIf="attemptUpdatedNotice && !quizSubmitted" class="alert alert-info" data-testid="attempt-updated-notice">
    <app-icon name="info" size="sm"></app-icon>
    <span class="flex-1">Your instructor updated this quiz since you opened it. Answers to questions that did not change were kept. Please review your answers before submitting.</span>
  </div>

  <!-- KAN-349: server-authoritative attempt deadline + attempt-cap states -->
  <ng-container *ngIf="!quizSubmitted && !isViewingPreviousSubmission">
    <div *ngIf="attemptExpired" class="alert alert-error" data-testid="attempt-expired-banner">
      <app-icon name="schedule" size="sm"></app-icon>
      <span class="flex-1">Time expired. This attempt can no longer be submitted.</span>
      <button *ngIf="!noAttemptsRemaining"
              type="button"
              class="btn btn-sm btn-primary"
              data-testid="start-fresh-attempt-button"
              (click)="startFreshAttempt()">Start fresh attempt</button>
    </div>

    <!-- KAN-469: three tiers instead of two. The five-minute warning is the
         heads-up the requirement asks for; the final minute keeps the louder
         KAN-349 state. Expiry behavior is unchanged. -->
    <div *ngIf="!attemptExpired && attemptRemainingSeconds() !== null"
         class="alert py-2"
         [class.alert-info]="countdownUrgency() === 'normal'"
         [class.alert-warning]="countdownUrgency() === 'warning'"
         [class.alert-error]="countdownUrgency() === 'critical'"
         data-testid="attempt-countdown">
      <app-icon name="schedule" size="sm"></app-icon>
      <span>Time remaining:
        <span class="font-mono font-semibold" data-testid="attempt-countdown-value">{{ formatRemaining(attemptRemainingSeconds()!) }}</span>
      </span>
      <span *ngIf="countdownUrgency() !== 'normal'" class="text-xs opacity-80" data-testid="attempt-countdown-warning">
        Finish up - time is nearly out.
      </span>
    </div>

    <div *ngIf="attemptsExhaustedAtStart" class="alert alert-error" data-testid="attempts-exhausted-banner">
      <app-icon name="error" size="sm"></app-icon>
      <span>
        No attempts remaining<ng-container *ngIf="attemptLimit !== null"> — you have used {{ attemptsUsed ?? attemptLimit }} of {{ attemptLimit }} attempts</ng-container>.
      </span>
    </div>
  </ng-container>

  <!-- KAN-414: a bank slot ({ slotType:'bank' }) is NOT a question — it has no
       type/text/options, so rendering it produces a card with a points badge
       above an empty body. Show a preparing state until the draw (learner
       attempt-start, or the instructor preview draw) resolves every slot.
       attemptSubmitBlocked covers every terminal state that leaves the slots
       unresolved with no draw coming (start failed, attempts exhausted, expired) —
       those render their own banner above, and a spinner beside them would
       promise questions that will never arrive. -->
  <div *ngIf="awaitingDraw && !attemptSubmitBlocked && !moduleLocked"
       class="alert" data-testid="quiz-panel-draw-pending">
    <span class="loading loading-spinner loading-sm"></span>
    <span>Preparing your questions...</span>
  </div>

  <!-- Quiz Questions -->
  <div class="quiz-questions space-y-4"
       *ngIf="quizQuestions.length > 0 && !awaitingDraw"
       [class.pointer-events-none]="quizSubmitting"
       [class.opacity-50]="quizSubmitting">
    <div *ngFor="let question of quizQuestions; let i = index; trackBy: trackByIndex"
         [id]="'question-' + i"
         class="question-block mb-6 p-4 rounded-lg bg-base-200">

      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <span class="badge badge-primary">{{ i + 1 }}</span>
          <p class="quiz-question-text font-medium flex-1" [innerHTML]="question.text | linkify"></p>
          <!-- KAN-242: acknowledgement is never scored — show a neutral "Not scored" badge instead of "Worth N pts" -->
          <span *ngIf="question.type === 'acknowledgement' || question.type === 'information-collection'" class="badge badge-ghost badge-sm whitespace-nowrap">Not scored</span>
          <span *ngIf="question.type !== 'acknowledgement' && question.type !== 'information-collection'" class="badge badge-secondary badge-sm whitespace-nowrap">Worth {{ getQuestionPoints(question) }} pts</span>
        </div>
        <!-- KAN-229: optional question-stem image -->
        <!-- KAN-265 AC3: rendered large + click/keyboard to open a full-size
             lightbox so small diagram labels stay legible without zooming. -->
        <img *ngIf="question.image?.url"
             class="quiz-stem-image"
             [src]="question.image?.url"
             [alt]="question.image?.alt || ''"
             role="button"
             tabindex="0"
             title="Click to enlarge"
             (click)="openLightbox(question.image?.url, question.image?.alt)"
             (keydown)="onStemImageKeydown($event, question.image?.url, question.image?.alt)" />
        <div class="pl-8">

          <!-- Multiple Choice -->
          <div *ngIf="question.type === 'multiple-choice'" class="space-y-2">
            <label *ngFor="let option of (question.options ?? []); let j = index; trackBy: trackByIndex"
                   class="quiz-answer-option flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-base-300 transition-colors border-2 border-transparent"
                   [class.border-success]="quizSubmitted && canShowIndicators() && quizFeedback[i]?.isCorrect && isAnswerSelected(i, j)"
                   [class.border-error]="quizSubmitted && canShowIndicators() && !quizFeedback[i]?.isCorrect && isAnswerSelected(i, j)"
                   [class.border-warning]="quizSubmitted && canShowAnswers() && !quizFeedback[i]?.isCorrect && quizFeedback[i]?.correctAnswer === j">
              <input type="radio"
                     [name]="'q' + i"
                     [value]="j"
                     (change)="selectAnswer(i, j)"
                     [checked]="isAnswerSelected(i, j)"
                     [disabled]="quizSubmitted"
                     class="radio radio-primary">
              <!-- KAN-665: image + text share one wrapping body beside the control, so a
                   narrow panel drops the text BELOW the image instead of squeezing it
                   into a one-character column. -->
              <span class="quiz-option-body">
                <!-- KAN-229 / REQ-QUIZ-110: optional option image — enlarged + click/keyboard
                     to open the shared full-size lightbox so diagram options are legible. -->
                <img *ngIf="option.image?.url"
                     class="quiz-option-image"
                     [src]="option.image?.url"
                     [alt]="option.image?.alt || ''"
                     role="button"
                     tabindex="0"
                     title="Click to enlarge"
                     (click)="$event.preventDefault(); $event.stopPropagation(); openLightbox(option.image?.url, option.image?.alt)"
                     (keydown)="onStemImageKeydown($event, option.image?.url, option.image?.alt)" />
                <span *ngIf="option.text" class="quiz-option-text" [innerHTML]="option.text | linkify"></span>
              </span>
              <span *ngIf="quizSubmitted && canShowAnswers() && quizFeedback[i]?.correctAnswer === j"
                    class="ml-auto text-sm font-semibold text-success">
                &#x2713; Correct
              </span>
            </label>
          </div>

          <!-- Select All That Apply -->
          <div *ngIf="question.type === 'select-all'" class="space-y-2">
            <p class="text-sm text-base-content/70 mb-2">Select all that apply:</p>
            <!-- Scoring mode hint — visible before and after submission -->
            <div class="scoring-mode-hint flex items-start gap-2 mb-3 p-2 rounded-lg bg-base-300 border-l-[3px] border-warning">
              <app-icon name="info" size="sm" class="text-warning mt-0.5 shrink-0"></app-icon>
              <div class="text-xs">
                <ng-container *ngIf="question.scoringMode === 'partial'; else allOrNothing">
                  <span class="text-base-content">Partial credit — wrong answers reduce your score</span>
                  <details class="mt-1">
                    <summary class="text-primary cursor-pointer">How is this scored?</summary>
                    <div class="mt-2 p-2 bg-base-200 rounded-md text-base-content/70">
                      <strong class="text-base-content">Formula:</strong> (Correct Selections - Incorrect Selections) / Total Correct Options &times; Points<br>
                      <strong class="text-base-content">Example:</strong> If 3 correct options exist, selecting 2 correct + 1 incorrect = (2-1)/3 &times; points = 33% of points<br>
                      <span class="text-error">Minimum score: 0 (can't go negative)</span>
                    </div>
                  </details>
                </ng-container>
                <ng-template #allOrNothing>
                  <span class="text-base-content">All or nothing — select all correct answers to earn points</span>
                  <details class="mt-1">
                    <summary class="text-primary cursor-pointer">How is this scored?</summary>
                    <div class="mt-2 p-2 bg-base-200 rounded-md text-base-content/70">
                      No partial credit is awarded. Selecting any wrong answer or missing any correct answer results in 0 points.
                    </div>
                  </details>
                </ng-template>
              </div>
            </div>
            <label *ngFor="let option of (question.options ?? []); let j = index; trackBy: trackByIndex"
                   class="quiz-answer-option flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-base-300 transition-colors border-2 border-transparent"
                   [class.border-success]="quizSubmitted && canShowIndicators() && isSelectAllOptionCorrect(i, j) && isAnswerSelected(i, j)"
                   [class.border-error]="quizSubmitted && canShowIndicators() && !isSelectAllOptionCorrect(i, j) && isAnswerSelected(i, j)"
                   [class.border-warning]="quizSubmitted && canShowAnswers() && !quizFeedback[i]?.isCorrect && isSelectAllOptionCorrect(i, j) && !isAnswerSelected(i, j)">
              <input type="checkbox"
                     [checked]="isAnswerSelected(i, j)"
                     (change)="selectAnswer(i, j)"
                     [disabled]="quizSubmitted"
                     class="checkbox checkbox-primary">
              <!-- KAN-665: image + text share one wrapping body beside the control, so a
                   narrow panel drops the text BELOW the image instead of squeezing it
                   into a one-character column. -->
              <span class="quiz-option-body">
                <!-- KAN-229 / REQ-QUIZ-110: optional option image — enlarged + click/keyboard
                     to open the shared full-size lightbox so diagram options are legible. -->
                <img *ngIf="option.image?.url"
                     class="quiz-option-image"
                     [src]="option.image?.url"
                     [alt]="option.image?.alt || ''"
                     role="button"
                     tabindex="0"
                     title="Click to enlarge"
                     (click)="$event.preventDefault(); $event.stopPropagation(); openLightbox(option.image?.url, option.image?.alt)"
                     (keydown)="onStemImageKeydown($event, option.image?.url, option.image?.alt)" />
                <span *ngIf="option.text" class="quiz-option-text" [innerHTML]="option.text | linkify"></span>
              </span>
              <span *ngIf="quizSubmitted && canShowAnswers() && isSelectAllOptionCorrect(i, j)"
                    class="ml-auto text-sm font-semibold text-success">
                &#x2713; Correct
              </span>
            </label>
          </div>

          <!-- Short Answer -->
          <div *ngIf="question.type === 'short-answer'" class="mt-2">
            <app-grading-rules-alert
              class="block mb-2"
              [caseSensitive]="!!question.caseSensitive"
              [trimWhitespace]="question.trimWhitespace !== false"
              [ignoreInternalWhitespace]="!!question.ignoreInternalWhitespace"
              [defaultExpanded]="false">
            </app-grading-rules-alert>
            <input type="text"
                   [(ngModel)]="userAnswers[i]"
                   (ngModelChange)="onAnswerModelChanged()"
                   [disabled]="quizSubmitted"
                   placeholder="Type your answer here..."
                   class="input input-bordered w-full">
          </div>

          <!-- KAN-436: keyless short text collected for instructor review. -->
          <div *ngIf="question.type === 'information-collection'"
               class="information-collection-question mt-2 p-3 rounded-lg bg-base-200 border-l-[3px] border-info">
            <div class="flex items-center gap-2 mb-2 text-xs text-base-content/70">
              <app-icon name="info" size="sm" class="text-info shrink-0"></app-icon>
              <span>This response is recorded but not scored.</span>
            </div>
            <input type="text"
                   data-testid="information-collection-input"
                   [(ngModel)]="userAnswers[i]"
                   (ngModelChange)="onAnswerModelChanged()"
                   [disabled]="quizSubmitted"
                   placeholder="Type your response here..."
                   class="input input-bordered w-full">
            <div *ngIf="quizSubmitted" class="mt-2 text-sm font-medium text-info flex items-center gap-1">
              <app-icon name="check_circle" size="sm" class="text-info"></app-icon>
              <span>Recorded</span>
            </div>
          </div>

          <!-- True/False -->
          <div *ngIf="question.type === 'true-false'" class="space-y-2">
            <div class="flex gap-4">
              <label class="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-base-300 transition-colors border-2 border-transparent"
                     [class.border-success]="quizSubmitted && canShowIndicators() && quizFeedback[i]?.isCorrect && userAnswers[i] === true"
                     [class.border-error]="quizSubmitted && canShowIndicators() && !quizFeedback[i]?.isCorrect && userAnswers[i] === true"
                     [class.border-warning]="quizSubmitted && canShowAnswers() && !quizFeedback[i]?.isCorrect && quizFeedback[i]?.correctAnswer === true">
                <input type="radio"
                       [name]="'q' + i"
                       [value]="true"
                       (change)="selectAnswer(i, true)"
                       [checked]="userAnswers[i] === true"
                       [disabled]="quizSubmitted"
                       class="radio radio-primary">
                <span>True</span>
                <span *ngIf="quizSubmitted && canShowAnswers() && quizFeedback[i]?.correctAnswer === true"
                      class="ml-auto text-sm font-semibold text-success">
                  <app-icon name="check" size="sm"></app-icon>
                </span>
              </label>
              <label class="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-base-300 transition-colors border-2 border-transparent"
                     [class.border-success]="quizSubmitted && canShowIndicators() && quizFeedback[i]?.isCorrect && userAnswers[i] === false"
                     [class.border-error]="quizSubmitted && canShowIndicators() && !quizFeedback[i]?.isCorrect && userAnswers[i] === false"
                     [class.border-warning]="quizSubmitted && canShowAnswers() && !quizFeedback[i]?.isCorrect && quizFeedback[i]?.correctAnswer === false">
                <input type="radio"
                       [name]="'q' + i"
                       [value]="false"
                       (change)="selectAnswer(i, false)"
                       [checked]="userAnswers[i] === false"
                       [disabled]="quizSubmitted"
                       class="radio radio-primary">
                <span>False</span>
                <span *ngIf="quizSubmitted && canShowAnswers() && quizFeedback[i]?.correctAnswer === false"
                      class="ml-auto text-sm font-semibold text-success">
                  <app-icon name="check" size="sm"></app-icon>
                </span>
              </label>
            </div>
          </div>

          <!-- Paragraph Answer -->
          <div *ngIf="question.type === 'paragraph'" class="mt-2">
            <div *ngIf="question.characterLimit || question.wordLimit" class="text-sm text-base-content/70 mb-2">
              <span *ngIf="question.wordLimit">Max {{ question.wordLimit }} words</span>
              <span *ngIf="question.wordLimit && question.characterLimit"> | </span>
              <span *ngIf="question.characterLimit">Max {{ question.characterLimit }} characters</span>
            </div>
            <textarea
              [(ngModel)]="userAnswers[i]"
              [disabled]="quizSubmitted"
              [maxlength]="question.characterLimit || 5000"
              placeholder="Type your answer here..."
              rows="6"
              class="textarea textarea-bordered w-full"
              (input)="onParagraphInput(i, $event)"></textarea>
            <div class="flex justify-between text-sm text-base-content/70 mt-1">
              <span *ngIf="!quizSubmitted" class="text-warning">
                <app-icon name="info" size="xs"></app-icon>
                This question requires manual grading
              </span>
              <span *ngIf="userAnswers[i] && (question.characterLimit || question.wordLimit)">
                <span *ngIf="question.wordLimit">{{ getWordCount(userAnswers[i]) }}/{{ question.wordLimit }} words</span>
                <span *ngIf="question.wordLimit && question.characterLimit"> | </span>
                <span *ngIf="question.characterLimit">{{ userAnswers[i].length }}/{{ question.characterLimit }} chars</span>
              </span>
            </div>
            <div *ngIf="quizSubmitted" class="alert alert-info mt-2">
              <app-icon name="info" size="sm"></app-icon>
              <span>This answer will be reviewed and graded manually by an instructor.</span>
            </div>
          </div>

          <!-- Multiple Choice Grid -->
          <div *ngIf="question.type === 'multiple-choice-grid'" class="mt-4">
            <!-- Scoring mode hint — visible before and after submission -->
            <div class="scoring-mode-hint flex items-start gap-2 mb-3 p-2 rounded-lg bg-base-300 border-l-[3px] border-warning" data-testid="grid-scoring-mode-hint">
              <app-icon name="info" size="sm" class="text-warning mt-0.5 shrink-0"></app-icon>
              <div class="text-xs">
                <span class="text-base-content">Partial credit — each correct row earns its points independently</span>
                <details class="mt-1">
                  <summary class="text-primary cursor-pointer">How is this scored?</summary>
                  <div class="mt-2 p-2 bg-base-200 rounded-md text-base-content/70">
                    Each row is graded on its own. Select every correct column for a row (a row may have more than one, or none); your selections must match exactly to earn that row's points. Incorrect rows earn 0 — there is no penalty for wrong rows.
                  </div>
                </details>
              </div>
            </div>
            <!-- Reviewing-state legend -->
            <div *ngIf="quizSubmitted && canShowGridFeedback(i)" class="grid-legend mb-2">
              <span class="grid-legend-item">
                <span class="ans-marker correct"></span> Correct &middot; you chose it
              </span>
              <span class="grid-legend-item">
                <span class="ans-marker correct-miss"></span> Correct &middot; you missed it
              </span>
              <span class="grid-legend-item">
                <span class="ans-marker wrong-pick"></span> You chose &middot; wrong
              </span>
              <span class="grid-legend-item">
                <span class="ans-marker"></span> &mdash;
              </span>
            </div>
            <div class="grid-table-outer">
              <div class="grid-scroll-wrap">
                <table class="grid-table">
                  <colgroup>
                    <col class="col-row-label">
                    <col *ngFor="let col of (question.gridColumns ?? []); trackBy: trackByIndex" class="col-value">
                  </colgroup>
                  <thead class="grid-thead">
                    <tr>
                      <th class="grid-th-label" aria-label=""></th>
                      <th *ngFor="let col of (question.gridColumns ?? []); trackBy: trackByIndex" class="grid-th-col">
                        <div class="cell-scroll" [innerHTML]="col.label | linkify"></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let row of (question.gridRows ?? []); let rowIndex = index; trackBy: trackByIndex"
                        class="grid-body-row"
                        [class.tint-success]="quizSubmitted && canShowGridFeedback(i) && isGridRowCorrect(i, rowIndex)"
                        [class.tint-danger]="quizSubmitted && canShowGridFeedback(i) && !isGridRowCorrect(i, rowIndex)">
                      <!-- Row label cell with optional points chip -->
                      <td class="grid-td-label">
                        <div class="grid-row-label-inner">
                          <ng-container *ngIf="quizSubmitted && canShowGridFeedback(i)">
                            <span *ngIf="isGridRowCorrect(i, rowIndex)"
                                  class="pts-chip pts-chip--success">+{{ getPerRowPoints(question, rowIndex) }}</span>
                            <span *ngIf="!isGridRowCorrect(i, rowIndex)"
                                  class="pts-chip pts-chip--danger">0</span>
                          </ng-container>
                          <div class="cell-scroll grid-row-label-text" [innerHTML]="row.label | linkify"></div>
                        </div>
                      </td>
                      <!-- Value cells -->
                      <td *ngFor="let col of (question.gridColumns ?? []); let colIndex = index; trackBy: trackByIndex"
                          class="grid-td-value">
                        <!-- Taking state: native checkbox (KAN-274). MC-grid is
                             multi-select — a learner may check 0, 1, or many
                             columns per row. Leaving every column unchecked is a
                             valid "no answer" for a no-answer row (AC2). -->
                        <input *ngIf="!quizSubmitted"
                               type="checkbox"
                               [attr.data-testid]="'mcgrid-cell-' + i + '-' + rowIndex + '-' + colIndex"
                               [checked]="isMcGridCellChecked(i, rowIndex, colIndex)"
                               (change)="selectMcGridCheckbox(i, rowIndex, colIndex)"
                               class="checkbox checkbox-sm checkbox-primary">
                        <!-- Reviewing state: custom marker -->
                        <ng-container *ngIf="quizSubmitted && canShowGridFeedback(i)">
                          <!-- correct answer AND user checked it -->
                          <span *ngIf="isGridCellCorrectAnswer(i, rowIndex, colIndex) && isMcGridCellChecked(i, rowIndex, colIndex)"
                                class="ans-marker correct"
                                title="Correct &middot; you chose it"></span>
                          <!-- correct answer AND user missed it -->
                          <span *ngIf="isGridCellCorrectAnswer(i, rowIndex, colIndex) && !isMcGridCellChecked(i, rowIndex, colIndex)"
                                class="ans-marker correct-miss"
                                title="Correct answer &middot; you missed it"></span>
                          <!-- incorrect column AND user checked it -->
                          <span *ngIf="!isGridCellCorrectAnswer(i, rowIndex, colIndex) && isMcGridCellChecked(i, rowIndex, colIndex)"
                                class="ans-marker wrong-pick"
                                title="You chose &middot; wrong"></span>
                          <!-- otherwise empty -->
                          <span *ngIf="!isGridCellCorrectAnswer(i, rowIndex, colIndex) && !isMcGridCellChecked(i, rowIndex, colIndex)"
                                class="ans-marker"></span>
                        </ng-container>
                        <!-- Reviewing state: feedback gated (score_only / indicators) — KAN-253.
                             Show ONLY the learner's own selections as static read-only markers.
                             No correctness coloring, no tooltip, no deferred-load placeholder. -->
                        <ng-container *ngIf="quizSubmitted && !canShowGridFeedback(i)">
                          <span *ngIf="isMcGridCellChecked(i, rowIndex, colIndex)"
                                class="ans-marker selected"
                                aria-label="Your selection"></span>
                          <span *ngIf="!isMcGridCellChecked(i, rowIndex, colIndex)"
                                class="ans-marker"></span>
                        </ng-container>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Checkbox Grid -->
          <div *ngIf="question.type === 'checkbox-grid'" class="mt-4">
            <!-- Scoring mode hint — visible before and after submission -->
            <div class="scoring-mode-hint flex items-start gap-2 mb-3 p-2 rounded-lg bg-base-300 border-l-[3px] border-warning" data-testid="grid-scoring-mode-hint">
              <app-icon name="info" size="sm" class="text-warning mt-0.5 shrink-0"></app-icon>
              <div class="text-xs">
                <span class="text-base-content">Partial credit — each correct row earns its points independently</span>
                <details class="mt-1">
                  <summary class="text-primary cursor-pointer">How is this scored?</summary>
                  <div class="mt-2 p-2 bg-base-200 rounded-md text-base-content/70">
                    Each row is graded on its own. All selections in a row must match exactly to earn that row's points; incorrect rows earn 0. There is no penalty for wrong rows.
                  </div>
                </details>
              </div>
            </div>
            <!-- Reviewing-state legend -->
            <div *ngIf="quizSubmitted && canShowGridFeedback(i)" class="grid-legend mb-2">
              <span class="grid-legend-item">
                <span class="ans-marker sq correct"></span> Correct &middot; you checked it
              </span>
              <span class="grid-legend-item">
                <span class="ans-marker sq correct-miss"></span> Correct &middot; you missed it
              </span>
              <span class="grid-legend-item">
                <span class="ans-marker sq wrong-pick"></span> You checked &middot; wrong
              </span>
              <span class="grid-legend-item">
                <span class="ans-marker sq"></span> &mdash;
              </span>
            </div>
            <div class="grid-table-outer">
              <div class="grid-scroll-wrap">
                <table class="grid-table">
                  <colgroup>
                    <col class="col-row-label">
                    <col *ngFor="let col of (question.gridColumns ?? []); trackBy: trackByIndex" class="col-value">
                  </colgroup>
                  <thead class="grid-thead">
                    <tr>
                      <th class="grid-th-label" aria-label=""></th>
                      <th *ngFor="let col of (question.gridColumns ?? []); trackBy: trackByIndex" class="grid-th-col">
                        <div class="cell-scroll" [innerHTML]="col.label | linkify"></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let row of (question.gridRows ?? []); let rowIndex = index; trackBy: trackByIndex"
                        class="grid-body-row"
                        [class.tint-success]="quizSubmitted && canShowGridFeedback(i) && isCheckboxGridRowCorrect(i, rowIndex)"
                        [class.tint-danger]="quizSubmitted && canShowGridFeedback(i) && !isCheckboxGridRowCorrect(i, rowIndex)">
                      <!-- Row label cell with optional points chip -->
                      <td class="grid-td-label">
                        <div class="grid-row-label-inner">
                          <ng-container *ngIf="quizSubmitted && canShowGridFeedback(i)">
                            <span *ngIf="isCheckboxGridRowCorrect(i, rowIndex)"
                                  class="pts-chip pts-chip--success">+{{ getPerRowPoints(question, rowIndex) }}</span>
                            <span *ngIf="!isCheckboxGridRowCorrect(i, rowIndex)"
                                  class="pts-chip pts-chip--danger">0</span>
                          </ng-container>
                          <div class="cell-scroll grid-row-label-text" [innerHTML]="row.label | linkify"></div>
                        </div>
                      </td>
                      <!-- Value cells -->
                      <td *ngFor="let col of (question.gridColumns ?? []); let colIndex = index; trackBy: trackByIndex"
                          class="grid-td-value">
                        <!-- Taking state: native checkbox -->
                        <input *ngIf="!quizSubmitted"
                               type="checkbox"
                               [checked]="userAnswers[i] && userAnswers[i][rowIndex] && userAnswers[i][rowIndex][colIndex]"
                               (change)="selectGridCheckbox(i, rowIndex, colIndex)"
                               class="checkbox checkbox-sm checkbox-primary">
                        <!-- Reviewing state: custom square marker -->
                        <ng-container *ngIf="quizSubmitted && canShowGridFeedback(i)">
                          <!-- correct answer AND user checked it -->
                          <span *ngIf="isCheckboxCellCorrectAnswer(i, rowIndex, colIndex) && isCheckboxCellUserSelected(i, rowIndex, colIndex)"
                                class="ans-marker sq correct"
                                title="Correct &middot; you checked it"></span>
                          <!-- correct answer AND user missed it -->
                          <span *ngIf="isCheckboxCellCorrectAnswer(i, rowIndex, colIndex) && !isCheckboxCellUserSelected(i, rowIndex, colIndex)"
                                class="ans-marker sq correct-miss"
                                title="Correct answer &middot; you missed it"></span>
                          <!-- incorrect answer AND user checked it -->
                          <span *ngIf="!isCheckboxCellCorrectAnswer(i, rowIndex, colIndex) && isCheckboxCellUserSelected(i, rowIndex, colIndex)"
                                class="ans-marker sq wrong-pick"
                                title="You checked &middot; wrong"></span>
                          <!-- otherwise empty -->
                          <span *ngIf="!isCheckboxCellCorrectAnswer(i, rowIndex, colIndex) && !isCheckboxCellUserSelected(i, rowIndex, colIndex)"
                                class="ans-marker sq"></span>
                        </ng-container>
                        <!-- Reviewing state: feedback gated (score_only / indicators) — KAN-253.
                             Show ONLY the cells the learner checked as static read-only markers.
                             No correctness coloring, no tooltip, no deferred-load placeholder. -->
                        <ng-container *ngIf="quizSubmitted && !canShowGridFeedback(i)">
                          <span *ngIf="isCheckboxCellUserSelected(i, rowIndex, colIndex)"
                                class="ans-marker sq selected"
                                aria-label="Your selection"></span>
                          <span *ngIf="!isCheckboxCellUserSelected(i, rowIndex, colIndex)"
                                class="ans-marker sq"></span>
                        </ng-container>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Matching (KAN-333) — pair each left item to a right item -->
          <div *ngIf="question.type === 'matching'" class="mt-4 matching-question">
            <!-- Scoring mode hint -->
            <div *ngIf="question.scoringMode !== 'all-or-nothing'"
                 class="scoring-mode-hint flex items-start gap-2 mb-3 p-2 rounded-lg bg-base-300 border-l-[3px] border-warning"
                 data-testid="matching-scoring-mode-hint">
              <app-icon name="info" size="sm" class="text-warning mt-0.5 shrink-0"></app-icon>
              <div class="text-xs">
                <span class="text-base-content">Partial credit — each correct pair earns an equal share of the points</span>
              </div>
            </div>
            <div class="space-y-2">
              <div *ngFor="let left of (question.matchLeft ?? []); let leftIndex = index; trackBy: trackByIndex"
                   class="matching-row flex items-center gap-3 p-2 rounded-lg"
                   [class.tint-success]="quizSubmitted && canShowGridFeedback(i) && isMatchingPairCorrect(i, leftIndex)"
                   [class.tint-danger]="quizSubmitted && canShowGridFeedback(i) && !isMatchingPairCorrect(i, leftIndex)"
                   [attr.data-testid]="'matching-row-' + leftIndex">
                <!-- Per-pair points chip (review, feedback-gated) -->
                <ng-container *ngIf="quizSubmitted && canShowGridFeedback(i)">
                  <span *ngIf="isMatchingPairCorrect(i, leftIndex)"
                        class="pts-chip pts-chip--success">+{{ getPerRowPoints(question, leftIndex) }}</span>
                  <span *ngIf="!isMatchingPairCorrect(i, leftIndex)"
                        class="pts-chip pts-chip--danger">0</span>
                </ng-container>
                <span class="matching-left text-sm" [innerHTML]="left.label | linkify"></span>
                <app-icon name="arrow_forward" size="sm" class="matching-arrow opacity-60 shrink-0"></app-icon>
                <!-- Taking state: right-item dropdown -->
                <select *ngIf="!quizSubmitted"
                        class="select select-bordered select-sm matching-select"
                        [value]="matchingAnswerFor(i, leftIndex)"
                        (change)="selectMatchingAnswer(i, leftIndex, $event)"
                        [attr.aria-label]="'Match for ' + (left.label | plainText)"
                        [attr.data-testid]="'matching-learner-select-' + leftIndex">
                  <option value="">-- Select --</option>
                  <option *ngFor="let right of (question.matchRight ?? []); let rightIndex = index; trackBy: trackByIndex"
                          [value]="rightIndex">{{ right.label | plainText }}</option>
                </select>
                <!-- Reviewing state: learner pick + correct answer hint.
                     KAN-665: right-hand labels are authored rich text, so they render through
                     linkify like `.matching-left` (a raw {{ }} showed literal ** after submit). -->
                <div *ngIf="quizSubmitted" class="matching-review flex flex-col gap-0.5">
                  <span class="matching-your-answer text-sm"
                        [class.text-success]="canShowGridFeedback(i) && isMatchingPairCorrect(i, leftIndex)"
                        [class.text-error]="canShowGridFeedback(i) && !isMatchingPairCorrect(i, leftIndex)"
                        [innerHTML]="(matchingLearnerLabel(i, leftIndex) || 'No answer') | linkify"></span>
                  <span *ngIf="quizSubmitted && canShowGridFeedback(i) && !isMatchingPairCorrect(i, leftIndex)"
                        class="matching-correct-answer text-xs text-success"
                        [attr.data-testid]="'matching-correct-' + leftIndex">Correct: <span [innerHTML]="matchingRightLabel(i, matchingCorrectIndexFor(i, leftIndex)) | linkify"></span></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Acknowledgement (KAN-242) — record-only, never scored.
               Renders a neutral "Not scored" info card. In review mode it shows a
               neutral "Recorded" state and NEVER green/red correct/incorrect borders. -->
          <div *ngIf="question.type === 'acknowledgement'"
               class="ack-question mt-2 p-3 rounded-lg bg-base-200 border-l-[3px] border-info">
            <div class="flex items-center gap-2 mb-2 text-xs text-base-content/70">
              <app-icon name="info" size="sm" class="text-info shrink-0"></app-icon>
              <span>This response is recorded but not scored.</span>
            </div>

            <!-- Checkbox mode: single affirm box -->
            <label *ngIf="(question.ackMode ?? 'checkbox') === 'checkbox'"
                   class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-base-300 transition-colors">
              <input type="checkbox"
                     [checked]="userAnswers[i] === true"
                     (change)="selectAnswer(i, !(userAnswers[i] === true))"
                     [disabled]="quizSubmitted"
                     class="checkbox checkbox-primary">
              <span class="quiz-ack-affirm" [innerHTML]="(question.affirmLabel || 'I acknowledge') | linkify"></span>
            </label>

            <!-- Choices mode: radio options (no correct-answer reveal, ever) -->
            <div *ngIf="question.ackMode === 'choices'" class="space-y-2">
              <label *ngFor="let option of (question.options ?? []); let j = index; trackBy: trackByIndex"
                     class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-base-300 transition-colors">
                <input type="radio"
                       [name]="'ack-q' + i"
                       [value]="j"
                       (change)="selectAnswer(i, j)"
                       [checked]="isAnswerSelected(i, j)"
                       [disabled]="quizSubmitted"
                       class="radio radio-primary">
                <span class="quiz-option-text" [innerHTML]="option.text | linkify"></span>
              </label>
            </div>

            <!-- Review mode: neutral recorded state, never correct/incorrect -->
            <div *ngIf="quizSubmitted" class="mt-2 text-sm font-medium text-info flex items-center gap-1">
              <app-icon name="check_circle" size="sm" class="text-info"></app-icon>
              <span>Recorded</span>
            </div>
          </div>

          <!-- Per-question Feedback (KAN-219 reopen):
               These auto-graded alerts ("Correct!", "Partial Credit", "Incorrect.")
               are per-question right/wrong indicators — they MUST be gated by
               `canShowIndicators()` so the `score_only` visibility level shows the
               aggregate score only and hides per-question right/wrong reveals.
               The "Awaiting manual grading" alert is a workflow notice (not a
               right/wrong reveal), so it remains visible at all levels.
               Short-answer "Expected: <answer>" reveal is gated by `canShowAnswers()`
               since it exposes the correct value.
               KAN-242: acknowledgement questions are never scored, so the entire
               auto-graded feedback block is suppressed for them. -->
          <div *ngIf="quizSubmitted && showFeedback && quizFeedback[i] && question.type !== 'acknowledgement' && question.type !== 'information-collection'" class="mt-3">
            <!-- Manual Grading Pending (workflow notice, always visible) -->
            <div *ngIf="quizFeedback[i].requiresManualGrading" class="alert alert-info">
              <app-icon name="pending" size="sm"></app-icon>
              <span>Awaiting manual grading ({{ getQuestionPoints(question) }} points)</span>
            </div>
            <!-- Auto-graded Correct -->
            <div *ngIf="!quizFeedback[i].requiresManualGrading && quizFeedback[i].isCorrect && canShowIndicators()" class="alert border-2 border-success bg-transparent">
              <app-icon name="check_circle" size="sm" class="text-success"></app-icon>
              <span>Correct! (+{{ quizFeedback[i].points | number:'1.0-2' }} points)</span>
            </div>
            <!-- Grid Question with Partial Credit -->
            <div *ngIf="!quizFeedback[i].requiresManualGrading && !quizFeedback[i].isCorrect && quizFeedback[i].status === 'partial' && canShowIndicators()" class="alert border-2 border-warning bg-transparent">
              <app-icon name="info" size="sm" class="text-warning"></app-icon>
              <span>Partial Credit: {{ quizFeedback[i].score | number:'1.0-2' }}/{{ quizFeedback[i].maxScore | number:'1.0-2' }} points</span>
            </div>
            <!-- Auto-graded Incorrect (excluding partial credit) -->
            <div *ngIf="!quizFeedback[i].requiresManualGrading && !quizFeedback[i].isCorrect && quizFeedback[i].status !== 'partial' && canShowIndicators()" class="alert border-2 border-error bg-transparent">
              <app-icon name="cancel" size="sm" class="text-error"></app-icon>
              <span>Incorrect.<ng-container *ngIf="question.type === 'short-answer' && canShowAnswers() && quizFeedback[i].correctAnswerDisplay"> Expected: {{ quizFeedback[i].correctAnswerDisplay }}</ng-container></span>
            </div>
            <!-- Per-question explanation (KAN-254): instructor-authored explanation
                 text, revealed ONLY at the indicators_answers_explanations
                 ("Full Feedback with Explanations") tier — getExplanationTexts()
                 returns [] at every stricter level. -->
            <div *ngIf="getExplanationTexts(i).length > 0" class="alert border-2 border-info bg-transparent mt-2 quiz-explanation">
              <app-icon name="info" size="sm" class="text-info"></app-icon>
              <div>
                <p class="font-semibold text-sm">Explanation</p>
                <p class="text-sm" *ngFor="let text of getExplanationTexts(i)">{{ text }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Quiz Actions (Sticky Footer) -->
<!-- KAN-414: !awaitingDraw — no answer/submit affordances for slots that have
     not been resolved into questions yet. Also excludes quizSubmitted: once
     submitted, the Quiz Results block below takes over (KAN-430 card #99). -->
<div class="quiz-actions p-4 pb-6" *ngIf="quizQuestions.length > 0 && !awaitingDraw && !quizSubmitted">
    <!-- Loading spinner while waiting for server scores -->
    <div *ngIf="quizSubmitting" class="flex flex-col items-center justify-center py-8 gap-3">
      <span class="loading loading-spinner loading-lg text-primary"></span>
      <p class="text-base-content/70">Submitting your answers...</p>
    </div>

    <!-- KAN-77 (AC1/AC2): pre-submit question-status review grid -->
    <app-question-status-grid
      *ngIf="!quizSubmitted && !quizSubmitting"
      [questionCount]="quizQuestions.length"
      [answered]="questionAnsweredFlags"
      (jumpToQuestion)="scrollToQuestion($event)">
    </app-question-status-grid>

    <div *ngIf="!quizSubmitted && !quizSubmitting">
      <button class="btn w-full transition-all duration-300"
              (click)="submitQuiz()"
              [disabled]="!canSubmitQuiz()"
              [class.btn-warning]="canOverrideVideoRequirement() && !videoCompleted && isVideoRequired && allQuestionsAnswered()"
              [class.btn-primary]="allQuestionsAnswered() && !(canOverrideVideoRequirement() && !videoCompleted && isVideoRequired)"
              [class.btn-disabled]="!canSubmitQuiz()"
              [class.opacity-40]="!canSubmitQuiz()"
              [style.cursor]="!canSubmitQuiz() ? 'not-allowed' : 'pointer'">
        <app-icon *ngIf="canOverrideVideoRequirement() && !videoCompleted && isVideoRequired"
                  name="admin_panel_settings" size="sm"></app-icon>
        <app-icon *ngIf="canSubmitQuiz()" name="check_circle" size="sm"></app-icon>
        Submit Quiz
        <span *ngIf="canOverrideVideoRequirement() && !videoCompleted && isVideoRequired" class="badge badge-xs badge-neutral ml-2">
          Instructor
        </span>
      </button>
      <!-- KAN-603: /80, not /60 -- 12px text on --b1 needs 4.5:1; /60 read
           3.45:1 in dark. Guarded by quiz-panel.theme.spec.ts. -->
      <p *ngIf="!canSubmitQuiz()" class="text-xs text-base-content/80 text-center mt-2">
        <span *ngIf="!allQuestionsAnswered()">Answer all questions</span>
        <span *ngIf="!allQuestionsAnswered() && isVideoRequired && !videoCompleted && !canOverrideVideoRequirement()"> and </span>
        <span *ngIf="isVideoRequired && !videoCompleted && !canOverrideVideoRequirement()">watch {{requiredWatchPercentage}}% of video</span>
        <span> to submit</span>
      </p>
    </div>
</div>

<!-- Quiz Results (Sticky Footer) -->
<!-- KAN-430 card #99: gated on quizSubmitted ALONE — deliberately NOT on
     !awaitingDraw. A submitted attempt already has a real recorded score
     even when quizQuestions still holds an unresolved bank slot: a legacy
     submission (predates the attempt-snapshot system, so the API returns
     questions: null — quiz-submission-retrieval.service.ts:823-825) leaves
     prefillQuizFromSubmission unable to overwrite quizQuestions
     (component.ts:1818), so it stays on the [Input]-bound, currently-authored
     definition — which can carry a bank slot added to the quiz AFTER the
     legacy submission was recorded. awaitingDraw exists to hide
     answer/submit affordances for an undrawn slot (KAN-414); those live in
     the block above and remain gated on it. Nothing in this block lets a
     learner interact with an unresolved slot, so lifting it out of the gate
     narrows the guard's SCOPE without weakening its CONDITION. -->
<div class="quiz-actions p-4 pb-6" *ngIf="quizSubmitted" data-testid="quiz-results-panel">
    <!-- Quiz Results -->
    <div *ngIf="quizSubmitted" class="space-y-4">
      <!-- Wrong Answer Feedback (canned message from instructor) -->
      <!-- KAN-311: a 0-point quiz is ungraded and cannot be failed, so the banner stays hidden. -->
      <div *ngIf="wrongAnswerFeedback && !quizPassed && !isUngradedQuiz" class="alert alert-warning">
        <app-icon name="feedback" size="sm"></app-icon>
        <div>
          <p class="font-semibold text-sm">Note from instructor:</p>
          <p class="text-sm">{{ wrongAnswerFeedback }}</p>
        </div>
      </div>

      <!-- KAN-327: Pass/Success Feedback (canned message from instructor) — the
           inverted mirror of the wrong-answer banner: shown ONLY on a PASS. A
           0-point quiz is ungraded and cannot be passed, so it stays hidden. -->
      <div *ngIf="passFeedback && quizPassed && !isUngradedQuiz" class="alert alert-success">
        <app-icon name="feedback" size="sm"></app-icon>
        <div>
          <p class="font-semibold text-sm">Note from instructor:</p>
          <p class="text-sm">{{ passFeedback }}</p>
        </div>
      </div>

      <!-- Partial Grading Notice -->
      <div *ngIf="isPartiallyGraded" class="alert alert-warning">
        <app-icon name="info" size="lg"></app-icon>
        <div>
          <h4 class="font-bold">Partial Results Available</h4>
          <p class="text-sm">{{ manualGradingPending }} question(s) require manual grading.</p>
          <p class="text-sm">Auto-graded score: <span class="badge badge-secondary">{{ autoGradedScore | scorePts:autoGradedMaxScore }}</span></p>
          <p class="text-sm font-semibold">You'll be notified when grading is complete.</p>
        </div>
      </div>

      <!-- KAN-627: prior completion credit is instructor credit, not an earned score -->
      <div *ngIf="!isPartiallyGraded && mode === 'learner' && viewingPriorCredit" class="alert border-2 border-success bg-transparent"
           data-testid="quiz-panel-prior-credit-result">
        <app-icon name="task_alt" size="lg" class="text-success"></app-icon>
        <div class="flex-1">
          <h4 class="font-bold text-lg">Credited for prior completion</h4>
          <p>Your instructor credited this quiz because you completed it previously. No score was recorded.</p>
        </div>
      </div>

      <!-- Full Results (when no manual grading) — Learner -->
      <div *ngIf="!isPartiallyGraded && mode === 'learner' && !viewingPriorCredit" class="alert border-2 bg-transparent"
           [class.border-success]="quizPassed"
           [class.border-error]="!quizPassed">
        <app-icon [name]="quizPassed ? 'school' : 'error'" size="lg"
                  [class.text-success]="quizPassed"
                  [class.text-error]="!quizPassed"></app-icon>
        <div class="flex-1">
          <h4 class="font-bold text-lg">
            {{ quizPassed ? 'Congratulations!' : 'Not Quite There' }}
          </h4>
          <p>
            Your score: <span class="badge badge-secondary">{{ autoGradedScore | scorePts:autoGradedMaxScore }}</span>
            <!-- KAN-127 Phase 1: show percentage after points, gated on autoGradedMaxScore > 0 (D5 guard) -->
            <span *ngIf="resultBannerScoreParts"
                  class="ml-2 text-sm opacity-70"
                  data-testid="quiz-result-percentage">({{ resultBannerScoreParts.percentText }})</span>
          </p>
          <p *ngIf="!quizPassed">You need {{ passingScore }}% or higher to pass.</p>
          <!-- KAN-314 VS1: attempts remaining / exhausted notice -->
          <p *ngIf="attemptLimit !== null && !noAttemptsRemaining" class="text-sm opacity-70" data-testid="attempts-remaining">
            Attempts remaining: {{ attemptsRemaining }} of {{ attemptLimit }}
          </p>
          <p *ngIf="noAttemptsRemaining" class="text-sm font-medium text-error" data-testid="no-attempts-remaining">
            No attempts remaining
          </p>
          <!-- KAN-478: a retake that scores lower than the pass the learner already
               holds changes nothing — completion is derived from the earliest VALID
               passing submission and every reporting surface reads the best attempt.
               Say so, or a red "Not Quite There" over a completed course reads as a
               lost pass. -->
          <p *ngIf="!quizPassed && quizEverPassed" class="text-sm opacity-70" data-testid="prior-pass-retained">
            Your earlier passing attempt still counts.
          </p>
        </div>
        <!-- KAN-314 VS1 / KAN-478: the attempt cap is the ONLY gate on a retake.
             resolveAttemptLimit already folds Allow Quiz Retakes (false -> 1) and
             Maximum Retakes (N -> N+1) into it, so gating on anything else here
             would be a second, divergent copy of the retake policy. Passing no
             longer withholds the button: the server never blocked a post-pass
             retake, and withholding it left "Attempts remaining: 10 of 11"
             advertising an affordance that did not exist. -->
        <button *ngIf="!noAttemptsRemaining"
                class="btn btn-outline btn-sm"
                (click)="retryQuiz()">
          <app-icon name="refresh" size="sm"></app-icon>
          Try Again
        </button>
      </div>

      <!-- Full Results (when no manual grading) — Instructor preview/test (neutral framing) -->
      <div *ngIf="!isPartiallyGraded && mode !== 'learner'" class="alert border-2 bg-transparent border-info">
        <app-icon name="school" size="lg" class="text-info"></app-icon>
        <div class="flex-1">
          <h4 class="font-bold text-lg">Preview Result</h4>
          <p>
            Score: <span class="badge badge-secondary">{{ autoGradedScore | scorePts:autoGradedMaxScore }}</span>
            <!-- KAN-127 Phase 1: add testid so both learner and instructor % spans are stable for e2e -->
            <span *ngIf="resultBannerScoreParts"
                  class="ml-2 text-sm opacity-70"
                  data-testid="quiz-result-percentage">({{ resultBannerScoreParts.percentText }})</span>
            <span *ngIf="!resultBannerScoreParts"
                  class="ml-2 text-sm opacity-70">({{ quizScore | number:'1.0-1' }}%)</span>
          </p>
          <p class="text-sm opacity-70">Learners need {{ passingScore }}% or higher to pass.</p>
        </div>
        <button class="btn btn-outline btn-sm" (click)="retryQuiz()">
          <app-icon name="refresh" size="sm"></app-icon>
          Try Again
        </button>
      </div>

      <!-- KAN-478: gated on quizEverPassed, not quizPassed. A learner viewing a
           post-pass retake that scored lower still holds their pass, so hiding
           forward navigation from them would dead-end a COMPLETED course. For a
           learner with no pass on record the two are identical (pre-fix behavior). -->
      <div class="flex gap-2">
        <!-- Complete module button (non-modal) -->
        <button *ngIf="!isModal && (quizEverPassed || isPartiallyGraded || !quizQuestions.length)"
                class="btn btn-success flex-1"
                (click)="completeModule.emit()">
          <app-icon name="check" size="sm"></app-icon>
          Complete Module
        </button>

        <!-- Modal navigation buttons -->
        <div *ngIf="isModal && (quizEverPassed || isPartiallyGraded || !quizQuestions.length)"
             class="flex gap-2 w-full">
          <button *ngIf="canGoToPrevious"
                  class="btn btn-outline flex-1"
                  (click)="navigatePrevious.emit()">
            <app-icon name="arrow_back" size="sm"></app-icon>
            Previous
          </button>
          <button *ngIf="canGoToNext"
                  class="btn btn-primary flex-1"
                  (click)="navigateNext.emit()">
            <app-icon name="arrow_forward" size="sm"></app-icon>
            Next
          </button>
          <!-- KAN-595: `!canGoToNext` is NOT "this is the last item" - it is also
               true when the next item is sequentially LOCKED, when the gate map
               has not loaded, and when courseStructure is null. `isLastItem` is
               the index-derived signal; only it may claim the course is over. -->
          <!-- KAN-597: completion is being saved - hold Next's footprint with a
               neutral pending state rather than a completion button the
               learner would click for nothing. -->
          <button *ngIf="!canGoToNext && completionPending"
                  class="btn btn-primary flex-1" disabled aria-busy="true">
            <span class="loading loading-spinner loading-sm" aria-hidden="true"></span>
            Saving progress...
          </button>
          <button *ngIf="!canGoToNext && !completionPending && isLastItem"
                  class="btn btn-success flex-1"
                  (click)="completeModule.emit()">
            <app-icon name="check" size="sm"></app-icon>
            Complete
          </button>
          <button *ngIf="!canGoToNext && !completionPending && !isLastItem"
                  class="btn btn-success flex-1"
                  (click)="completeModule.emit()">
            <app-icon name="check" size="sm"></app-icon>
            Complete Module
          </button>
        </div>
      </div>

      <!-- KAN-388: terminal failure verdict + explicit exit. Renders ONLY when
           the learner is genuinely stranded (isStrandedFailure: last item,
           never passed, attempts exhausted, not awaiting manual grading —
           KAN-478 narrowed "failed" to quizEverPassed, so a learner sitting on
           a lower-scoring post-pass retake is not stranded). The modal
           Next/Complete block above is gated on quizEverPassed so it renders
           nothing here, and "Try Again" is withheld once attempts are spent —
           without this the learner dead-ends on the bare modal X with no
           verdict or path forward. Display + close ONLY: it emits closePlayer
           (the existing exit mechanism) and NEVER completes the module/course
           or issues a certificate (backend gating stands, KAN-362/364). -->
      <!-- KAN-487: `grid-flow-row grid-cols-1`, not `flex-col items-stretch`.
           daisyUI's `.alert` is display:grid with grid-auto-flow:column, so the
           flex utilities were inert: the alert kept dealing columns and the
           full-width Exit action landed in a 74px column beside the message,
           clipping its own label (scrollHeight 54 vs clientHeight 30 — it read
           as "Exit" cut through the middle). -->
      <div *ngIf="isStrandedFailure"
           class="alert alert-error grid-flow-row grid-cols-1 gap-3"
           data-testid="stranded-failure-verdict">
        <div class="flex items-start gap-2 w-full">
          <!-- KAN-487: no `text-error` here. DaisyUI paints `.alert-error` with
               oklch(var(--er)) and `.text-error` with the SAME oklch(var(--er)),
               so an accented glyph on a filled error surface measures 1.00:1 —
               invisible. Bare, it inherits the alert's paired `--erc` foreground
               (6.83:1 in both themes), matching the attempts-exhausted banner. -->
          <app-icon name="error" size="lg"></app-icon>
          <div class="flex-1">
            <h4 class="font-bold text-lg" data-testid="stranded-failure-heading">
              You did not pass this course
            </h4>
            <p class="text-sm">
              You did not pass the required quiz and no attempts remain, so this
              course cannot be completed.
            </p>
            <p class="text-sm mt-1" data-testid="stranded-failure-guidance">
              Contact your instructor to request a reset or additional attempts.
            </p>
          </div>
        </div>
        <!-- KAN-487: this button ships BLACK-on-red — `oklch(var(--erc))` fill
             with an `oklch(var(--er))` label, from the `.alert-error .btn` rule
             in quiz-panel.component.scss. Measured 6.82:1 both for the fill
             against the alert surface and for the label against the fill, in
             both themes. Do not read `btn btn-sm` here as "a default button":
             it is deliberately unstyled in the template so the surface-aware
             SCSS rule is the single place its colours are decided.
             `btn-error` was removed. It was already inert — `.quiz-actions .btn`
             (0,2,0) beat `.btn-error` (0,1,0) — but it was a live landmine: it
             promised an --er fill on the --er alert surface, which is 1.00:1.
             The neutral `--b2` button that `.quiz-actions .btn` would otherwise
             paint was measured and REJECTED: 5.45:1 in dark but only 2.75:1 in
             light, under WCAG 1.4.11's 3:1 for a control boundary. -->
        <button class="btn btn-sm w-full"
                (click)="closePlayer.emit()"
                data-testid="stranded-failure-exit">
          <app-icon name="logout" size="sm"></app-icon>
          Exit Course
        </button>
      </div>
    </div>
</div>

<!-- Complete Module Button (No Quiz) - Sticky Footer -->
<!-- KAN-196: Show the Complete/Next button as soon as the watch requirement is
     satisfied — not only at video end. Three independent ways the requirement
     can be satisfied:
       1. videoCompleted   — handler sets this true when watchPercentage >=
                             requiredWatchPercentage (covers partial 10-90% req
                             AND backend-persisted completion from prior session).
       2. videoEnded       — preserved for the 100%-required edge case where the
                             handler only marks videoCompleted=true on the same
                             tick as the (ended) event.
       3. !isVideoRequired — None / 0% req: button visible immediately. -->
<div *ngIf="quizQuestions.length === 0 && !moduleLocked && (videoCompleted || videoEnded || !isVideoRequired)" class="quiz-actions p-4 pb-6">
    <!-- Non-modal completion -->
    <button *ngIf="!isModal" class="btn btn-success w-full" (click)="completeModule.emit()">
      <app-icon name="check" size="sm"></app-icon>
      Complete Module
    </button>

    <!-- Modal navigation for video-only modules -->
    <div *ngIf="isModal" class="flex gap-2">
      <button *ngIf="canGoToPrevious"
              class="btn btn-outline flex-1"
              (click)="navigatePrevious.emit()">
        <app-icon name="arrow_back" size="sm"></app-icon>
        Previous
      </button>
      <button *ngIf="canGoToNext"
              class="btn btn-primary flex-1"
              (click)="navigateNext.emit()">
        <app-icon name="arrow_forward" size="sm"></app-icon>
        Next
      </button>
      <!-- KAN-595 (reported locus): this button read "Complete Course" on
           module 1 of 3 of a SEQUENTIAL course, because `!canGoToNext` was
           standing in for "last item" while modules 2 and 3 were still LOCKED.
           It is not a render flash - the label was stable and wrong until the
           next module unlocked. `isLastItem` now decides the label; the gate
           itself is unchanged. -->
      <!-- KAN-597: the watch requirement is met on screen but the save that
           completes the module has not answered yet. -->
      <button *ngIf="!canGoToNext && completionPending"
              class="btn btn-primary flex-1" disabled aria-busy="true">
        <span class="loading loading-spinner loading-sm" aria-hidden="true"></span>
        Saving progress...
      </button>
      <button *ngIf="!canGoToNext && !completionPending && isLastItem"
              class="btn btn-success flex-1"
              (click)="completeModule.emit()">
        <app-icon name="check" size="sm"></app-icon>
        Complete Course
      </button>
      <button *ngIf="!canGoToNext && !completionPending && !isLastItem"
              class="btn btn-success flex-1"
              (click)="completeModule.emit()">
        <app-icon name="check" size="sm"></app-icon>
        Complete Module
      </button>
    </div>
</div>

<!-- KAN-265 AC3 / KAN-229 (REQ-QUIZ-110): shared full-size image lightbox for
     both question-stem and option images. Click backdrop or press Escape to
     dismiss; the image renders contained so small diagram labels are readable
     without browser zoom. -->
<app-image-lightbox *ngIf="lightboxImageUrl"
                    [url]="lightboxImageUrl"
                    [alt]="lightboxImageAlt"
                    (closed)="closeLightbox()"></app-image-lightbox>
